// VESR (Virtual Election Situation Room) Incidents API contract.
// Mirrors api.vesr's IncidentDocument (camelCase over the wire) — bodies are
// bare documents (no envelope) except the list/nearby endpoints noted below.

export type IncidentCategory = 'Security' | 'Logistics' | 'Electoral' | 'Technical' | 'Political' | 'Other';

export type IncidentSeverity = 'Critical' | 'High' | 'Medium' | 'Low' | 'Info';

export type IncidentStatus = 'Reported' | 'Acknowledged' | 'InProgress' | 'Resolved' | 'Closed';

export interface IVesrLocation {
	stateCode?: string | null;
	stateName?: string | null;
	lgaCode?: string | null;
	lgaName?: string | null;
	wardCode?: string | null;
	pollingUnitCode?: string | null;
	eflocCode?: string | null;
	latitude?: number | null;
	longitude?: number | null;
}

export interface IVesrAttachment {
	id: string;
	fileName: string;
	fileUrl: string;
	thumbnailUrl?: string | null;
	contentType: string;
	fileSizeBytes: number;
	caption?: string | null;
	description?: string | null;
	uploadedBy?: string | null;
	uploadedByName?: string | null;
	uploadedAt: string;
}

export interface IVesrStatusHistory {
	fromStatus: string;
	toStatus: string;
	changedBy?: string | null;
	changedByName?: string | null;
	reason?: string | null;
	changedAt: string;
}

export interface IIncident {
	id: string;
	/** Human-readable sequential id — the agent-facing report number. */
	referenceId: string;
	title: string;
	description?: string | null;
	category: IncidentCategory;
	severity: IncidentSeverity;
	status: IncidentStatus;
	location: IVesrLocation;
	attachments: IVesrAttachment[];
	statusHistory: IVesrStatusHistory[];
	/** Deterministic GUID derived from the core user id server-side. */
	reportedBy?: string | null;
	reportedByName?: string | null;
	assignedTo?: string | null;
	assignedToName?: string | null;
	electionId?: string | null;
	resolutionNotes?: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface ICreateIncidentRequest {
	title: string;
	description?: string;
	category: IncidentCategory;
	severity: IncidentSeverity;
	stateCode?: string;
	stateName?: string;
	lgaCode?: string;
	lgaName?: string;
	wardCode?: string;
	pollingUnitCode?: string;
	eflocCode?: string;
	latitude?: number;
	longitude?: number;
	/**
	 * The core election id in string form ("2003"). api.vesr moved off GUIDs in
	 * the ElectionIdToCoreStringId migration — a report sent without this is
	 * stored unscoped and is invisible to every election-scoped situation-room
	 * query and to the `election_{id}` SignalR room.
	 */
	electionId?: string | null;
}

export interface IIncidentListResponse {
	data: IIncident[];
	pagination: {
		page: number;
		pageSize: number;
		totalCount: number;
		totalPages: number;
	};
}

export interface INearbyIncident {
	incident: IIncident;
	distanceKm: number;
	distanceDisplay: string;
}

export interface INearbyResponse {
	data: INearbyIncident[];
	count: number;
}

// Standalone-client outcome wrapper (same discipline as agent-assignment-api):
// non-2xx responses resolve so callers can branch on httpStatus; `reachable:
// false` means the network/service was unreachable.
export type IVesrOutcome<T> =
	| { reachable: false }
	| {
			reachable: true;
			httpStatus: number;
			body?: T;
			/** Populated on 429 from the RateLimitExceeded envelope / Retry-After header. */
			retryAfterSeconds?: number;
			/** True when X-Idempotency-Replayed was set (a retried create was deduped). */
			replayed?: boolean;
			/** Best-effort `{ message }` from non-2xx bodies. */
			errorMessage?: string;
	  };

// ---------------------------------------------------------------------------
// UI catalogs — single source for the form tiles, chips and badges.
// Color values are CSS custom property names (see src/index.css).
// ---------------------------------------------------------------------------

export interface ICategoryOption {
	value: IncidentCategory;
	label: string;
	helper: string;
	/** CSS var used as the tile/chip accent. */
	accent: string;
}

export const INCIDENT_CATEGORIES: ICategoryOption[] = [
	{
		value: 'Security',
		label: 'Security',
		helper: 'Violence, intimidation, ballot snatching',
		accent: 'var(--error-500)',
	},
	{
		value: 'Logistics',
		label: 'Logistics',
		helper: 'Late materials, missing officials, transport',
		accent: 'var(--warning-500)',
	},
	{
		value: 'Electoral',
		label: 'Electoral',
		helper: 'Vote buying, over-voting, result disputes',
		accent: 'var(--primary-500)',
	},
	{
		value: 'Technical',
		label: 'Technical',
		helper: 'BVAS failure, device or network problems',
		accent: 'var(--info-500)',
	},
	{
		value: 'Political',
		label: 'Political',
		helper: 'Party clashes, campaigning at the unit',
		accent: 'var(--accent-500)',
	},
	{
		value: 'Other',
		label: 'Other',
		helper: 'Anything else worth flagging',
		accent: 'var(--neutral-500)',
	},
];

export interface ISeverityOption {
	value: IncidentSeverity;
	label: string;
	helper: string;
	/** CSS var for the filled pill / chip dot. */
	color: string;
	bg: string;
}

/** Ordered ascending — rendered left-to-right as an intensity ramp. */
export const INCIDENT_SEVERITIES: ISeverityOption[] = [
	{ value: 'Info', label: 'Info', helper: 'Worth noting', color: 'var(--sev-info)', bg: 'var(--sev-info-bg)' },
	{ value: 'Low', label: 'Low', helper: 'Minor issue', color: 'var(--sev-low)', bg: 'var(--sev-low-bg)' },
	{ value: 'Medium', label: 'Medium', helper: 'Needs attention', color: 'var(--sev-medium)', bg: 'var(--sev-medium-bg)' },
	{ value: 'High', label: 'High', helper: 'Serious problem', color: 'var(--sev-high)', bg: 'var(--sev-high-bg)' },
	{
		value: 'Critical',
		label: 'Critical',
		helper: 'Emergency — act now',
		color: 'var(--sev-critical)',
		bg: 'var(--sev-critical-bg)',
	},
];

/** Lifecycle order — drives the mini status stepper on report cards. */
export const INCIDENT_STATUS_ORDER: IncidentStatus[] = ['Reported', 'Acknowledged', 'InProgress', 'Resolved', 'Closed'];

export const INCIDENT_STATUS_LABELS: Record<IncidentStatus, string> = {
	Reported: 'Reported',
	Acknowledged: 'Acknowledged',
	InProgress: 'In progress',
	Resolved: 'Resolved',
	Closed: 'Closed',
};

/** Maps a status to the app's badge tone vocabulary. */
export const INCIDENT_STATUS_TONES: Record<IncidentStatus, 'neutral' | 'info' | 'warning' | 'success'> = {
	Reported: 'neutral',
	Acknowledged: 'info',
	InProgress: 'warning',
	Resolved: 'success',
	Closed: 'neutral',
};

export const MAX_INCIDENT_PHOTOS = 10;
export const MAX_INCIDENT_TITLE = 500;
export const MAX_INCIDENT_DESCRIPTION = 4000;

// Video evidence: one clip per report, capped client-side AND server-side
// (api.vesr enforces the same MIME allowlist, 500MB and 1-video limits).
//
// MAX_VIDEO_BYTES must never exceed the server cap: api.vesr rejects at 500MB
// (IncidentService.MaxVideoSizeBytes) and the request/gateway/nginx limits sit
// just above that. Raising this side first only converts a fast, readable
// rejection into a long upload that fails at the end.
export const MAX_INCIDENT_VIDEOS = 1;
export const MAX_VIDEO_BYTES = 500 * 1024 * 1024;
// 3 minutes. api.vesr has NO duration check, so this is the only duration gate:
// it stops the in-app recorder and refuses longer files picked from the device.
// Android and iOS hold the same number.
export const MAX_VIDEO_DURATION_SEC = 180;
export const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
